/* eslint-disable no-console */
import memoize from "lodash/memoize";

interface DatadogRUM {
  getInternalContext?: () => { session_id: string };
}

declare global {
  interface Window {
    DD_LOGS?: DatadogRUM;
  }
}

export type LogLevel = "debug" | "info" | "warn" | "error";

export interface Logger {
  debug(message: string, data?: Record<string, unknown>): void;
  info(message: string, data?: Record<string, unknown>): void;
  warn(message: string, data?: Record<string, unknown>): void;
  error(message: string, error?: Error, data?: Record<string, unknown>): void;
}

type Metadata = {
  env: string;
  service: string;
};

type Meta = {
  env: {
    VITE_REACT_APP_ENV: string;
    VITE_REACT_APP_METRICS_SERVICE: string;
    VITE_LOG_LEVEL?: string;
  };
};

const getMetadata = (): Metadata => {
  const variables = (import.meta as unknown as Meta).env;

  return {
    env: variables.VITE_REACT_APP_ENV,
    service: variables.VITE_REACT_APP_METRICS_SERVICE,
  };
};

const getLogLevel = (): LogLevel => {
  const variables = (import.meta as unknown as Meta).env;
  const level = variables.VITE_LOG_LEVEL?.toLowerCase();

  if (
    level === "debug" ||
    level === "info" ||
    level === "warn" ||
    level === "error"
  ) {
    return level;
  }

  return "info";
};

function getStackTraceEntries(skipFrames = 2, numEntries = 1): string[] {
  try {
    const stack = new Error().stack || "";
    const lines = stack
      .split("\n")
      .slice(skipFrames + 1)
      .map((line) => line.trim())
      .map((line) => line.replace(/^\s*at\s*/, ""))
      .map((line) =>
        line.replace(/https?:\/\/[^/]+(\/[^:)]*)([:)]|$)/, "$1$2"),
      );

    return lines.slice(0, numEntries);
  } catch (error) {
    console.error(
      `Error getting stack trace entries: ${error}
This does not prevent the browser logger from logging but stacktrace entries will be missing!`,
    );
    return [];
  }
}

function getDDSessionID(): string {
  return window.DD_LOGS?.getInternalContext?.()?.session_id || "";
}

// Log level hierarchy (lower number = higher priority)
const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  error: 0,
  warn: 1,
  info: 2,
  debug: 3,
};

const shouldLog = memoize(
  (messageLevel: LogLevel, configuredLogLevel: LogLevel): boolean => {
    const configuredPriority = LOG_LEVEL_PRIORITY[configuredLogLevel];
    const messagePriority = LOG_LEVEL_PRIORITY[messageLevel];

    // Log if message priority is higher or equal to configured level (lower number = higher priority)
    return messagePriority <= configuredPriority;
  },
  (messageLevel: LogLevel, configuredLogLevel: LogLevel) =>
    `${messageLevel}-${configuredLogLevel}`,
);

const formatLogEntry = (
  level: LogLevel,
  message: string,
  stackTrace: string[] = [],
  data?: Record<string, unknown>,
): {
  timestamp: string;
  metadata: Metadata;
  sessionId: string;
  level: LogLevel;
  message: string;
  stackTrace: string[];
  data?: Record<string, unknown>;
} => ({
  timestamp: new Date().toISOString(),
  metadata: getMetadata(),
  sessionId: getDDSessionID(),
  level,
  message,
  stackTrace,
  ...(data && { data }),
});

export const logger: Logger = {
  debug(message: string, data?: Record<string, unknown>): void {
    const logLevel = getLogLevel();
    if (!shouldLog("debug", logLevel)) return;
    const stackEntries = getStackTraceEntries();
    const logEntry = formatLogEntry("debug", message, stackEntries, data);

    console.debug(logEntry);
  },

  info(message: string, data?: Record<string, unknown>): void {
    const logLevel = getLogLevel();
    if (!shouldLog("info", logLevel)) return;
    const stackEntries = getStackTraceEntries();
    const logEntry = formatLogEntry("info", message, stackEntries, data);

    console.info(logEntry);
  },

  warn(message: string, data?: Record<string, unknown>): void {
    const logLevel = getLogLevel();
    if (!shouldLog("warn", logLevel)) return;
    const stackEntries = getStackTraceEntries();
    const logEntry = formatLogEntry("warn", message, stackEntries, data);

    console.warn(logEntry);
  },

  error(message: string, error?: Error, data?: Record<string, unknown>): void {
    const logLevel = getLogLevel();
    if (!shouldLog("error", logLevel)) return;
    const stackEntries =
      error?.stack?.split("\n") || getStackTraceEntries(2, 20);
    const logEntry = formatLogEntry("error", message, stackEntries, data);

    console.error(logEntry, error);
  },
};

export default logger;
